/* eslint-disable @typescript-eslint/no-explicit-any */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getBackendUrl } from "@src/utils/getBackendUrl";
import { getAuthHeaders } from "@src/utils/getAuthHeader";

interface ReportsState {
	headliners: Array<any>;
	venues: Array<any>;
	currentPage: number;
	pageSize: number;
	marketplace: string;
	tableType: "MAPPED" | "UNMAPPED";
	total: number;
	loading: "idle" | "pending" | "succeeded" | "failed";
	error: string | null;
	itemsPerPage: number;
}

const initialState: ReportsState = {
	headliners: [],
	venues: [],
	currentPage: 1,
	pageSize: 30,
	marketplace: "TICKET_MASTER",
	tableType: "MAPPED",
	total: 0,
	loading: "idle",
	error: null,
	itemsPerPage: 30,
};

export const fetchReports = createAsyncThunk(
	"reports/fetchReports",
	async ({ page, pageSize, marketplace, tableType, endpoint }: {
		page: number;
		pageSize: number;
		marketplace: string;
		tableType: "MAPPED" | "UNMAPPED";
		endpoint: "marketplace-headliners" | "marketplace-venues"
	}) => {
		const response = await axios.get(`${getBackendUrl()}/${endpoint}`, {
			params: {
				page,
				pageSize,
				marketplace,
				tableType,
			},
		});
		return response.data;
	},
);

export const bulkMapUnmapVenue = createAsyncThunk(
	"events/bulkMapUnmapVenue",
	async (params: {
		venueMappings: Array<{ venueId: string; mappedStageFrontVenueId: string }>;
		action: "map" | "unmap"
	}, { rejectWithValue }) => {
		const { venueMappings, action } = params;

		try {
			const response = await axios.post(`${getBackendUrl()}/marketplace-venues/bulk`, {
				venueMappings,
				action,
			}, { headers: getAuthHeaders() });

			if (!response.data.success) {
				return rejectWithValue(response.data.error || `Failed to ${action} venues`);
			}

			return { venueMappings, ...response.data };
		} catch (error) {
			return rejectWithValue(`Failed to ${action} venues: ` + error);
		}
	},
);

export const bulkMapUnmapHeadliner = createAsyncThunk(
	"events/bulkMapUnmapHeadliner",
	async (params: {
		headlinerMappings: Array<{ headlinerId: string; mappedStageFrontHeadlinerId: string }>;
		action: "map" | "unmap"
	}, { rejectWithValue }) => {
		const { headlinerMappings, action } = params;

		try {
			const response = await axios.post(`${getBackendUrl()}/marketplace-headliners/bulk`, {
				headlinerMappings,
				action,
			}, { headers: getAuthHeaders() });

			if (!response.data.success) {
				return rejectWithValue(response.data.error || `Failed to ${action} headliners`);
			}

			return { headlinerMappings, ...response.data };
		} catch (error) {
			return rejectWithValue(`Failed to ${action} headliners: ` + error);
		}
	},
);

export const bulkDeleteHeadliners = createAsyncThunk(
	"events/bulkDeleteHeadliners",
	async (headlinerIds: Array<string>, { rejectWithValue }) => {
		try {
			await axios.delete(`${getBackendUrl()}/marketplace-headliners`, {
				data: {
					"headlinerIds": headlinerIds,
				},
				headers: getAuthHeaders(),
			});
			return { headlinerIds };
		} catch (error) {
			return rejectWithValue("Failed to delete headliners: " + error);
		}
	},
);

export const bulkDeleteVenues = createAsyncThunk(
	"events/bulkDeleteVenues",
	async (venueIds: Array<string>, { rejectWithValue }) => {
		try {
			await axios.delete(`${getBackendUrl()}/marketplace-venues`, {
				data: {
					"venueIds": venueIds,
				},
				headers: getAuthHeaders(),
			});
			return { venueIds };
		} catch (error) {
			return rejectWithValue("Failed to delete venues: " + error);
		}
	},
);

export const deleteVenue = createAsyncThunk("events/deleteEvent", async (venueId: string, { rejectWithValue }) => {
	try {
		await axios.delete(`${getBackendUrl()}/marketplace-venues`, {
			data: {
				"venueIds": [venueId],
			},
			headers: getAuthHeaders(),
		});
		return venueId;
	} catch (error) {
		return rejectWithValue("Failed to delete event" + error);
	}
});

export const deleteHeadliner = createAsyncThunk("events/deleteHeadliner", async (headlinerId: string, { rejectWithValue }) => {
	try {
		await axios.delete(`${getBackendUrl()}/marketplace-headliners`, {
			data: {
				"headlinerIds": [headlinerId],
			},
			headers: getAuthHeaders(),
		});
		return headlinerId;
	} catch (error) {
		return rejectWithValue("Failed to delete headliner" + error);
	}
});

export const reportsSlice = createSlice({
	name: "reports",
	initialState,
	reducers: {
		setCurrentPage(state, action) {
			state.currentPage = action.payload;
		},
		setPageSize(state, action) {
			state.pageSize = action.payload;
		},
		setMarketplace(state, action) {
			state.marketplace = action.payload;
		},
		setTableType(state, action) {
			state.tableType = action.payload;
		},
		setItemsPerPage: (state, action) => {
			state.itemsPerPage = action.payload;
			state.currentPage = 1;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchReports.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(fetchReports.fulfilled, (state, action) => {
				state.loading = "succeeded";
				if (action.meta.arg.endpoint === "marketplace-venues") {
					state.venues = action.payload.items;
				} else {
					state.headliners = action.payload.items;
				}
				state.total = action.payload.total;
			})
			.addCase(fetchReports.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.error.message || "Failed to fetch reports";
			});
	},
});

export const { setCurrentPage, setPageSize, setMarketplace, setTableType } = reportsSlice.actions;

export default reportsSlice.reducer;